/**
 * Shell-wide session data. Each is its own query so one failing never blanks
 * the others; all are cached under `sessionKeys` and cleared on logout.
 */
import { queryOptions, useQuery } from '@tanstack/react-query';
import { shellApi } from '@/api/endpoints/shell';
import { sessionKeys } from '@/api/keys';
import type { CurrentUser, UserRole } from '@/api/types';
import { identifyAccount, identifyUser } from '@/lib/analytics';

/** Query options, reusable from route guards via `queryClient.fetchQuery(sessionQueries.x())`. */
export const sessionQueries = {
  // Each fetch also tells analytics who this is (apps/web store/index.ts
  // fetchMe / fetchAccount); both are no-ops without consent or in a support session.
  me: () =>
    queryOptions({
      queryKey: sessionKeys.me(),
      queryFn: async ({ signal }) => {
        const user = await shellApi.me(signal);
        identifyUser(user);
        return user;
      },
      staleTime: 5 * 60_000,
    }),
  account: () =>
    queryOptions({
      queryKey: sessionKeys.account(),
      queryFn: async ({ signal }) => {
        const account = await shellApi.account(signal);
        identifyAccount(account);
        return account;
      },
      staleTime: 5 * 60_000,
    }),
  /** Billing reloads on window focus and every 5 minutes while the shell is mounted (parity §1). */
  billing: () =>
    queryOptions({
      queryKey: sessionKeys.billing(),
      queryFn: ({ signal }) => shellApi.billingSummary(signal),
      refetchOnWindowFocus: true,
      refetchInterval: 5 * 60_000,
    }),
  /** False on any failure: the operator check is an allowlist, never default-open. */
  predictionOperator: () =>
    queryOptions({
      queryKey: sessionKeys.predictionOperator(),
      queryFn: async ({ signal }) => {
        try {
          return (await shellApi.predictionAdminIdentity(signal)).is_operator;
        } catch {
          return false;
        }
      },
    }),
  connections: () =>
    queryOptions({ queryKey: sessionKeys.connections(), queryFn: ({ signal }) => shellApi.connections(signal) }),
};

export const useCurrentUser = () => useQuery(sessionQueries.me());
export const useAccount = () => useQuery(sessionQueries.account());
export const useBillingSummary = () => useQuery(sessionQueries.billing());
export const usePredictionOperator = () => useQuery(sessionQueries.predictionOperator());
export const useConnections = () => useQuery(sessionQueries.connections());

export const isAccountAdmin = (role: UserRole | undefined): boolean => role === 'admin' || role === 'owner';

export const displayName = (user: CurrentUser | undefined): string => {
  if (!user) return '';
  const full = [user.name, user.surname].filter(Boolean).join(' ').trim();
  return full || user.email_address;
};

export const initials = (user: CurrentUser | undefined): string => {
  if (!user) return '';
  const parts = [user.name, user.surname].filter((p): p is string => Boolean(p?.trim()));
  const source = parts.length ? parts.map((p) => p.trim()[0]) : [user.email_address[0]];
  return source.join('').slice(0, 2).toUpperCase();
};
